import { useCallback, useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { MultiStepFormContext, useForm } from '@kanda-libs/ks-component-ts';
import { useToast } from '@kanda-libs/ks-design-library';
import { FinanceRate, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import useApiError from 'hooks/useApiError';
import {
  ALWAYS_SHOW_OMNI_ERROR_CODES,
  ERROR_1190,
  MAXIMUM_OMNI_ERROR_CODE_TO_DISPLAY,
} from 'pages/ApplyForFinance/Pages/YourInfo/constants';
import {
  capitalise,
  extractErrorMessage,
  getErrorCode,
  getLatestLoanPayment,
} from 'utils';

import { formatDataForApi } from './helpers';

export default function useAdditionalInfoForm() {
  const {
    data: { job, credit },
  } = useContext(MultiStepFormContext);

  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useToast();
  const onError = useApiError();
  const { push } = useHistory();

  const [success, setSuccess] = useState<boolean>(false);
  const [showButton, setShowButton] = useState<boolean>(false);

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      credit,
      bankruptcy_iva: undefined,
      confirm_account: undefined,
    },
  });

  // Refs to hold timers
  const timerRef = useRef<NodeJS.Timer | null>(null);
  const buttonTimerRef = useRef<NodeJS.Timer | null>(null);
  // Function to redirect through timers
  const redirect = useCallback(() => {
    dispatch(
      actions.getJob({
        params: { id: job.id },
        forceReload: true,
      }),
    ).then(() => {
      // Redirect
      timerRef.current = setTimeout(
        () => push(URLS.status.replace(':id?', job.id)),
        5000,
      );
      // Show button after 10s if not redirected
      buttonTimerRef.current = setTimeout(() => setShowButton(true), 10000);
    });
  }, [push, job, dispatch]);

  const onSubmit = useCallback(
    (finalData) => {
      const rate = job?.customer_options?.finance_option;
      const latestLoanPayment = getLatestLoanPayment(job);
      const id = latestLoanPayment?.id;

      if (!rate) {
        showError(
          'Error completing finance application, a finance rate is not attached to this job - please contact Kanda or try again later',
        );
      }
      if (!id) {
        showError(
          'Error completing finance application, no payment record exists to attach your info to - please contact Kanda or try again later',
        );
      }

      const body = formatDataForApi(finalData, rate as FinanceRate, true);

      setIsSubmitting(true);

      dispatch(
        actions.putCredit({
          body,
          params: {
            id,
          },
          forceReload: true,
          onError,
          onSuccess: () => {
            dispatch(
              actions.applyCredit({
                params: { id },
                forceReload: true,
                onError: (error) => {
                  const code = getErrorCode(error.message);
                  const message = extractErrorMessage(error);

                  if (message.includes('1190')) {
                    showError(ERROR_1190);
                    return;
                  }

                  if (
                    code &&
                    (code < MAXIMUM_OMNI_ERROR_CODE_TO_DISPLAY ||
                      ALWAYS_SHOW_OMNI_ERROR_CODES.indexOf(code) !== -1)
                  ) {
                    if (message) {
                      showError(capitalise(message));
                      return;
                    }
                  }

                  setIsSubmitting(false);
                  showError('Error completing finance application');
                },
                onSuccess: () => {
                  setIsSubmitting(false);
                  setSuccess(true);
                  redirect();
                },
              }),
            );
          },
        }),
      );
    },
    [showError, job, redirect, dispatch, onError],
  );

  return {
    isSubmitting,
    form,
    onSubmit,
    onError,
    success,
    showButton,
    job,
    redirect,
  };
}
